import React, { useEffect, useState } from 'react';
import { toast } from 'react-hot-toast';
import { ChevronDown } from 'lucide-react';

import { useAppDispatch } from '@/lib/hooks';
import { setActivityDescription, setActivityName } from '@/lib/slices/activity-slice';
import AssignmentService, {
  AssignmentData,
  SubmissionData,
} from '@/services/course/assignment-service';
import LockedContent from '@/common/components/LockedContent';
import EmbeddedFileViewer from './components/EmbeddedFileViewer';
import AssignmentControls from './components/AssignmentControls';
import { BlockingContent, CourseId, SubunitId, UnitId } from 'course-types';

interface Props {
  assignmentId: string;
  courseId: CourseId;
  unitId?: UnitId;
  subunitId?: SubunitId;
  activityName?: string;
  activityDescription?: string;
}

function AssignmentHandler({ assignmentId, courseId }: Props) {
  const dispatch = useAppDispatch();

  const [assignment, setAssignment] = useState<AssignmentData | null>(null);
  const [submission, setSubmission] = useState<SubmissionData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isLocked, setIsLocked] = useState<boolean>(false);
  const [blockingContent, setBlockingContent] = useState<BlockingContent>({} as BlockingContent);
  const [assignmentFileBlob, setAssignmentFileBlob] = useState<Blob | null>(null);
  const [assignmentFileMimeType, setAssignmentFileMimeType] = useState<string>('');
  const [isDescriptionExpanded, setIsDescriptionExpanded] = useState(false);
  const [isDescriptionExpandable, setIsDescriptionExpandable] = useState(false);
  const descriptionRef = React.useRef<HTMLDivElement>(null);

  useEffect(() => {
    const fetchAssignment = async () => {
      try {
        setIsLoading(true);
        const data = await AssignmentService.getAssignment(assignmentId);

        if (!data) {
          return;
        }

        if (data.isLocked) {
          setIsLocked(true);
          setBlockingContent(data.blockingContent || ({} as BlockingContent));
          setAssignment(null);
          return;
        }

        if (!data.assignment) {
          return;
        }

        setIsLocked(false);
        setBlockingContent({} as BlockingContent);

        if (data.assignment?.s3_url) {
          const fileResult = await AssignmentService.fetchAssignmentFile(assignmentId);

          // Set assignment file blob
          if (fileResult) {
            setAssignmentFileBlob(fileResult.blob);
            setAssignmentFileMimeType(fileResult.mimeType);
          }
        }
        setAssignment(data.assignment);
        setSubmission(data.submission || null);

        // Update lesson slice with assignment info
        dispatch(setActivityName(data.assignment.name));
        dispatch(setActivityDescription(data.assignment.description));
      } catch (error) {
        console.error('Error fetching assignment:', error);
        toast.error('Failed to load assignment');
      } finally {
        setIsLoading(false);
      }
    };

    if (assignmentId) {
      fetchAssignment();
    }
  }, [assignmentId, dispatch]);

  // Check if description exceeds 3 lines
  useEffect(() => {
    if (descriptionRef.current && assignment?.description) {
      const lineHeight = parseFloat(getComputedStyle(descriptionRef.current).lineHeight);
      const height = descriptionRef.current.scrollHeight;
      const lines = Math.round(height / lineHeight);
      setIsDescriptionExpandable(lines > 3);
    }
  }, [assignment?.description]);

  if (isLoading) {
    return <div>Loading...</div>;
  }

  if (isLocked) {
    return <LockedContent blockingContent={blockingContent} courseId={courseId} />;
  }

  if (!assignment) {
    return (
      <div className='flex items-center justify-center min-h-[400px]'>
        <p className='text-text_gray_700'>Assignment not found</p>
      </div>
    );
  }

  return (
    <div className='w-full h-full flex flex-col overflow-y-auto'>
      <div className='flex flex-1 flex-wrap gap-6 px-4 pb-4 min-h-0'>
        {/* Left Side - Embedded Viewer */}
        <div className='flex-1 min-w-[400px] flex flex-col min-h-0 overflow-y-auto'>
          <div
            className={`bg-white border border-border_gray_300 rounded-lg flex flex-col ${assignment.s3_url ? 'flex-1 min-h-[600px]' : ''}`}
          >
            {/* Activity Description */}
            {assignment.description && assignment.description.trim().length > 0 && (
              <div className='flex-shrink-0'>
                <div className='p-6'>
                  <div
                    className={`flex items-center gap-2 mb-2 ${
                      isDescriptionExpandable ? 'cursor-pointer' : ''
                    }`}
                    onClick={() =>
                      isDescriptionExpandable && setIsDescriptionExpanded(!isDescriptionExpanded)
                    }
                  >
                    {isDescriptionExpandable && (
                      <ChevronDown
                        className={`w-5 h-5 text-text_gray_700 transition-transform duration-200 ${
                          isDescriptionExpanded ? 'rotate-180' : ''
                        }`}
                      />
                    )}
                    <h3 className='font-medium text-text_gray_900'>Description</h3>
                  </div>
                  <div
                    ref={descriptionRef}
                    className={`break-words whitespace-pre-wrap text-sm leading-5 text-text_gray_700 ${
                      isDescriptionExpandable && !isDescriptionExpanded ? 'line-clamp-3' : ''
                    } ${isDescriptionExpanded ? 'max-h-96 overflow-y-auto' : ''}`}
                  >
                    {assignment.description}
                  </div>
                </div>
                {assignment?.s3_url && <hr className='border-border_gray_300' />}
              </div>
            )}
            {assignment?.s3_url && (
              <div className='flex-1 p-6 min-h-0 h-full'>
                <div className='h-full'>
                  <EmbeddedFileViewer
                    fileBlob={assignmentFileBlob}
                    mimeType={assignmentFileMimeType}
                    emptyMessage='No assignment file available for preview'
                    fileName={assignment?.name}
                  />
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Right Side - Controls and Information */}
        <div className='w-96 min-w-96 flex-shrink-0 flex flex-col min-h-0'>
          <AssignmentControls
            assignment={assignment}
            submission={submission}
            setSubmission={setSubmission}
            courseId={courseId}
            assignmentFileBlob={assignmentFileBlob}
          />
        </div>
      </div>
    </div>
  );
}

export default AssignmentHandler;
